import Project from '@/mstore/types/project';
import { CaretDownOutlined, FolderAddOutlined } from '@ant-design/icons';
import { Button, Dropdown, MenuProps, Space, Typography } from 'antd';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useStore } from 'App/mstore';
import { hasSiteId, siteChangeAvailable } from 'App/routes';
import { useLocation } from 'App/routing';
import { mobileScreen } from 'App/utils/isMobile';
import { useModal } from 'Components/ModalContext';
import { Icon } from 'UI';

// Modal content, but the header renders this dropdown on every page, so an
// eager import parked its antd Form/Input/Segmented tree in the entry chunk.
const ProjectForm = React.lazy(
  () => import('Components/Client/Projects/ProjectForm'),
);

const { Text } = Typography;

function ProjectDropdown() {
  const mstore = useStore();
  const { t } = useTranslation();
  const {
    projectsStore,
    searchStore,
    searchStoreLive,
    userStore,
    aiFiltersStore,
  } = mstore;
  const { account } = userStore;
  const sites = projectsStore.list;
  const { siteId } = projectsStore;
  const { setSiteId } = projectsStore;
  const { initProject } = projectsStore;
  const location = useLocation();
  const isAdmin = account.admin || account.superAdmin;
  const activeSite = sites.find((s) => s.id === siteId);
  const showCurrent =
    hasSiteId(location.pathname) || siteChangeAvailable(location.pathname);
  const { openModal, closeModal } = useModal();

  const handleSiteChange = async (newSiteId: string) => {
    mstore.initClient();
    aiFiltersStore.clearFilters();
    setSiteId(newSiteId);
    searchStore.clearSearch();
    searchStore.clearList();
    searchStoreLive.clearSearch();

    // await customFieldStore.fetchList(newSiteId);
    // await searchStore.fetchSavedSearchList()
  };

  const onClose = (pro: Project) => {
    console.log('onClose', pro);
    closeModal();
    if (pro.projectId) {
      void handleSiteChange(pro.projectId.toString());
    }
  };

  const addProjectClickHandler = () => {
    initProject({});
    openModal(
      <React.Suspense fallback={null}>
        <ProjectForm onClose={onClose} />
      </React.Suspense>,
      { title: 'New Project' },
    );
  };

  const menuItems: MenuProps['items'] = sites.map((site) => ({
    key: site.id,
    label: (
      <div key={site.id} className="py-1! flex! items-center! gap-2!">
        <Icon
          name={site.platform === 'web' ? 'browser/browser' : 'mobile'}
          color={activeSite?.host === site.host ? 'main' : undefined}
        />
        <Text
          className={cn(
            'capitalize',
            activeSite?.host === site.host ? 'text-main' : '',
          )}
        >
          {site.host}
        </Text>
      </div>
    ),
  }));
  if (isAdmin) {
    menuItems?.unshift(
      {
        key: 'add-proj',
        label: (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <FolderAddOutlined rev={undefined} />
            <Text>{t('Add Project')}</Text>
          </div>
        ),
      },
      {
        type: 'divider',
      },
    );
  }

  const mobile = mobileScreen;
  return (
    <Dropdown
      menu={{
        items: menuItems,
        selectable: true,
        defaultSelectedKeys: [siteId],
        style: {
          maxHeight: 500,
          overflowY: 'auto',
        },
        onClick: (e) => {
          if (e.key === 'add-proj') {
            addProjectClickHandler();
          } else {
            void handleSiteChange(e.key);
          }
        },
      }}
      placement="bottomLeft"
    >
      <Button data-test-id="project-dropdown">
        <Space>
          <Text className="font-medium capitalize">
            {showCurrent && activeSite ? (
              <div className="text-ellipsis overflow-hidden whitespace-nowrap max-w-32 sm:max-w-full sm:flex sm:items-center sm:gap-2">
                {mobile ? null : (
                  <Icon
                    name={
                      activeSite?.platform === 'web'
                        ? 'browser/browser'
                        : 'mobile'
                    }
                  />
                )}
                {activeSite.host}
              </div>
            ) : (
              t('All Projects')
            )}
          </Text>
          <CaretDownOutlined rev={undefined} />
        </Space>
      </Button>
    </Dropdown>
  );
}

export default observer(ProjectDropdown);
